<template>
  <v-container>
    <v-layout row>
      <v-flex>
        <v-card-text>
          <v-container>

            <div class="page-header">
              <div>
                <h2>IABF {{ adminFestivalYear }} — Competition Registrations</h2>
                <p class="page-hint">After awards, enter place and score on each row. That writes dancer history for this year.</p>
                <p v-if="importFillNote" class="page-hint import-fill-note">{{ importFillNote }}</p>
              </div>
              <div>
                <v-btn outlined class="mr-2" @click="showEngravingsDialog = true">🏅 Engravings</v-btn>
                <v-btn outlined class="mr-2" @click="downloadCsv">↓ CSV</v-btn>
              </div>
            </div>

            <p v-if="resultError" class="warn-row mt-2">{{ resultError }}</p>

            <!-- Summary -->
            <div class="summary-bar mt-4 mb-4">
              <div class="summary-stat">
                <span class="stat-num">{{ rows.length }}</span>
                <span class="stat-label">Entries</span>
              </div>
              <div class="summary-stat">
                <span class="stat-num">{{ totalDancersCount }}</span>
                <span class="stat-label">Dancers</span>
              </div>
              <div class="summary-stat">
                <span class="stat-num">{{ rowsWithDivision.length }}</span>
                <span class="stat-label">Division Assigned</span>
              </div>
              <div class="summary-stat">
                <span class="stat-num">{{ rows.length - rowsWithDivision.length }}</span>
                <span class="stat-label">Division Pending</span>
              </div>
              <div class="summary-stat">
                <span class="stat-num">{{ rowsWithResult.length }}</span>
                <span class="stat-label">Place entered</span>
              </div>
              <div class="summary-stat">
                <span class="stat-num">{{ specialAwardsAssignedCount }}</span>
                <span class="stat-label">Judges' Choice</span>
              </div>
              <div class="summary-stat">
                <span class="stat-num">{{ rowsMissingMusic.length }}</span>
                <span class="stat-label">Music Missing</span>
              </div>
            </div>

            <!-- Medal-mode toggle -->
            <div class="medal-mode-toggle mb-3">
              <span class="medal-mode-label">Medal scheme:</span>
              <button type="button" class="medal-mode-chip" :class="{ active: !useFullMedals }" @click="useFullMedals = false">Limited (size-based)</button>
              <button type="button" class="medal-mode-chip" :class="{ active: useFullMedals }" @click="useFullMedals = true">Full (1·2·3 if ≥ 3, 1·2 if 2, 1 if 1)</button>
            </div>

            <div class="jc-panel mb-4">
              <div class="jc-head">
                <div>
                  <strong>Judges' Choice awards</strong>
                  <p class="muted small jc-lede">
                    Add the award names now. After judging, pick the winning piece.
                    A piece can win more than one.
                  </p>
                </div>
                <span class="muted small">{{ specialAwardsAssignedCount }} of {{ specialAwards.length }} assigned</span>
              </div>
              <table v-if="specialAwards.length" class="jc-table">
                <thead>
                  <tr>
                    <th>Award</th>
                    <th>Winning piece</th>
                    <th>Dancers</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="award in specialAwards" :key="award.id">
                    <td>
                      <input
                        class="jc-name-input"
                        type="text"
                        :value="award.name"
                        :disabled="jcSaving"
                        placeholder="Award name"
                        @change="onAwardNameChange(award, $event)"
                      >
                    </td>
                    <td>
                      <select
                        class="jc-piece-select"
                        :value="award.pieceId"
                        :disabled="jcSaving || !jcPieceOptions.length"
                        @change="onAwardPieceChange(award, $event)"
                      >
                        <option value="">— not assigned —</option>
                        <option
                          v-for="opt in jcPieceOptions"
                          :key="opt.pieceId"
                          :value="opt.pieceId"
                        >{{ opt.label }}</option>
                      </select>
                    </td>
                    <td class="jc-dancers">{{ dancersLabelForAward(award) || '—' }}</td>
                    <td>
                      <button
                        type="button"
                        class="jc-del"
                        title="Remove award"
                        :disabled="jcSaving"
                        @click="removeAward(award)"
                      >✕</button>
                    </td>
                  </tr>
                </tbody>
              </table>
              <p v-else class="muted small jc-empty">No Judges' Choice awards yet.</p>
              <div class="jc-add">
                <input
                  v-model="newAwardName"
                  class="jc-name-input"
                  type="text"
                  placeholder="e.g. Collective Chemistry"
                  :disabled="jcSaving"
                  @keydown.enter.prevent="addAward"
                >
                <v-btn small color="primary" dark :disabled="jcSaving || !newAwardName.trim()" @click="addAward">
                  + Add award
                </v-btn>
                <span v-if="jcNote" class="muted small">{{ jcNote }}</span>
              </div>
            </div>

            <!-- Scorecards to print -->
            <div v-if="rows.length" class="scorecard-panel mb-4">
              <div class="scorecard-head">
                <strong>Scorecards</strong>
                <span class="muted small">{{ expectedPdfPageCount }} PDF pages</span>
                <v-spacer />
                <v-btn small color="primary" @click="openScorecardPdf">📄 Build personalized scorecards PDF</v-btn>
              </div>
              <div v-if="skippedCompetitors.length" class="scorecard-skipped">
                <div class="scorecard-skipped-head">
                  ⚠️ {{ skippedCompetitors.length }} competitor{{ skippedCompetitors.length === 1 ? '' : 's' }} will be skipped from the PDF (no division yet):
                </div>
                <ul class="scorecard-skipped-list">
                  <li v-for="(s, i) in skippedCompetitors" :key="i">
                    <strong>{{ s.dancerName }}</strong>
                    <span class="muted small"> — {{ s.pieceTitle }} ({{ s.reason }})</span>
                  </li>
                </ul>
              </div>
              <div v-if="invisibleCompetitors.length" class="scorecard-skipped">
                <div class="scorecard-skipped-head">
                  ⚠️ {{ invisibleCompetitors.length }} dancer{{ invisibleCompetitors.length === 1 ? '' : 's' }} look like competitors but aren't in any row:
                </div>
                <ul class="scorecard-skipped-list">
                  <li v-for="(s, i) in invisibleCompetitors" :key="i">
                    <strong>{{ s.dancerName }}</strong>
                    <span class="muted small"> — {{ s.reasons }}</span>
                  </li>
                </ul>
              </div>
              <details class="scorecard-roster">
                <summary>
                  <strong>{{ pdfCompetitorNames.length }} competitors will be in the PDF</strong>
                  <span class="muted small"> (click to expand)</span>
                </summary>
                <ol class="scorecard-roster-list">
                  <li v-for="name in pdfCompetitorNames" :key="name">{{ name }}</li>
                </ol>
              </details>
            </div>

            <!-- Personalized scorecard PDF dialog -->
            <v-dialog v-model="showScorecardPdfDialog" max-width="640" scrollable>
              <v-card>
                <v-card-title>Build personalized scorecards PDF</v-card-title>
                <v-card-text>
                  <p class="muted small">
                    Pick each judge's three scorecard templates (PDF) below — one per division per judge, nine files total.
                    The output is one big PDF: all competitors for Judge 1 first, then Judge 2, then Judge 3. Each entry is
                    two pages (front = stamped scorecard, back = the dancer's blurb). Print duplex (long edge) and cut.
                  </p>
                  <p class="muted small">
                    For groups: one card per entry, listing all entrants on the front and stacking their blurbs on the back.
                  </p>

                  <div v-for="(judgeKey, idx) in judgeKeys" :key="judgeKey" class="judge-block">
                    <div class="judge-heading">Judge {{ idx + 1 }} templates</div>
                    <div class="template-row">
                      <label class="template-label">Classical Variation</label>
                      <input type="file" accept="application/pdf" @change="onTemplateChosen(judgeKey, 'classical', $event)" />
                      <span v-if="templates[judgeKey].classical" class="muted small">✓ {{ templates[judgeKey].classical.fileName }}</span>
                    </div>
                    <div class="template-row">
                      <label class="template-label">Contemporary</label>
                      <input type="file" accept="application/pdf" @change="onTemplateChosen(judgeKey, 'contemporary', $event)" />
                      <span v-if="templates[judgeKey].contemporary" class="muted small">✓ {{ templates[judgeKey].contemporary.fileName }}</span>
                    </div>
                    <div class="template-row">
                      <label class="template-label">Original Ballet Choreo <span class="muted small">(also used for Groups)</span></label>
                      <input type="file" accept="application/pdf" @change="onTemplateChosen(judgeKey, 'original', $event)" />
                      <span v-if="templates[judgeKey].original" class="muted small">✓ {{ templates[judgeKey].original.fileName }}</span>
                    </div>
                  </div>

                  <details class="template-row">
                    <summary><strong>Where to put the dancer name on the template</strong></summary>
                    <p class="muted small">
                      Coordinates are PDF points measured from the bottom-left of the page. After a test print, tweak these
                      to land in the right spot. They auto-save to your browser.
                    </p>
                    <div class="slot-grid">
                      <label class="slot-cell">
                        <span>Dancer X</span>
                        <input type="number" v-model.number="slot.dancerX" @change="saveSlot" />
                      </label>
                      <label class="slot-cell">
                        <span>Dancer Y</span>
                        <input type="number" v-model.number="slot.dancerY" @change="saveSlot" />
                      </label>
                      <label class="slot-cell">
                        <span>Dancer size</span>
                        <input type="number" v-model.number="slot.dancerSize" @change="saveSlot" />
                      </label>
                    </div>
                    <v-btn x-small outlined class="mt-2" :loading="buildingScorecardPdf" @click="buildScorecardPdf(true)">
                      Preview with first entry only
                    </v-btn>
                  </details>

                  <div v-if="scorecardPdfError" class="error--text mt-2">{{ scorecardPdfError }}</div>
                </v-card-text>
                <v-card-actions>
                  <v-btn text @click="showScorecardPdfDialog = false">Close</v-btn>
                  <v-spacer />
                  <v-btn
                    color="primary"
                    :loading="buildingScorecardPdf"
                    :disabled="!canBuildScorecardPdf"
                    @click="buildScorecardPdf"
                  >
                    Build &amp; download PDF
                  </v-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>

            <!-- By Division -->
            <div v-if="byDivision.length" class="division-bar mb-4">
              <div v-for="d in byDivision" :key="d.name" class="division-stat" :title="medalLabel(d.count)">
                <span class="stat-num">{{ d.count }}</span>
                <span class="stat-label">{{ d.name }}</span>
                <span class="stat-medal">{{ medalLabel(d.count) }}</span>
              </div>
            </div>

            <!-- Division-grouped view -->
            <div class="division-view">
              <div v-if="loading" class="muted">Loading…</div>
              <div v-else-if="rows.length === 0" class="muted">No competition registrations yet.</div>
              <div v-for="group in divisionGroups" :key="group.name" class="division-card">
                <div class="division-card-header" :style="{ borderLeftColor: hexColorFor(group.name) }">
                  <div class="division-card-title">
                    {{ group.name }}
                    <span class="division-card-count">· {{ group.competitors.length }} {{ group.competitors.length === 1 ? 'entry' : 'entries' }}</span>
                  </div>
                  <div class="division-card-medal">{{ medalLabel(group.competitors.length) || 'Unassigned' }}</div>
                </div>
                <table class="competitor-table">
                  <thead>
                    <tr>
                      <th>Dancer</th>
                      <th>Piece</th>
                      <th title="Self-reported ballet journey">Journey</th>
                      <th title="Shoes / pointe duration">Shoes</th>
                      <th title="Turning ability">Turn</th>
                      <th title="Balance & control">Balance</th>
                      <th title="Combination execution">Combo</th>
                      <th>Prev Gold</th>
                      <th>Place</th>
                      <th>Score</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="r in group.competitors" :key="r.rowKey">
                      <td>
                        <div v-for="e in r.entrants" :key="e.dancerId" class="entrant-line">
                          <router-link :to="'/iabf/admin/dancers/edit/' + e.dancerId" class="link">
                            {{ e.dancerName }}
                          </router-link>
                        </div>
                      </td>
                      <td>
                        <router-link v-if="r.pieceId" :to="'/iabf/admin/pieces/edit/' + r.pieceId" class="link">
                          {{ r.pieceTitle }}
                        </router-link>
                        <span v-else-if="r.registeredPieceTitle" class="muted">Registered: {{ r.registeredPieceTitle }} — confirm</span>
                        <span v-else class="muted">(none selected)</span>
                        <div v-if="r.entrants.length > 1" class="entry-badge">Group entry · {{ r.entrants.length }} dancers</div>
                      </td>
                      <td>
                        <div v-for="e in r.entrants" :key="e.dancerId" class="entrant-line">
                          <span v-if="e.signals.journey" class="signal-pill" :class="'pill-' + e.signals.journeyRank">{{ e.signals.journey }}</span>
                        </div>
                      </td>
                      <td>
                        <div v-for="e in r.entrants" :key="e.dancerId" class="entrant-line">
                          <span v-if="e.signals.shoes" class="signal-pill" :class="e.signals.isPointe ? 'pill-pointe' : 'pill-flat'">{{ e.signals.shoes }}</span>
                        </div>
                      </td>
                      <td>
                        <div v-for="e in r.entrants" :key="e.dancerId" class="entrant-line">
                          <span v-if="e.signals.turn" class="signal-pill" :class="'pill-' + e.signals.turnRank">{{ e.signals.turn }}</span>
                        </div>
                      </td>
                      <td>
                        <div v-for="e in r.entrants" :key="e.dancerId" class="entrant-line">
                          <span v-if="e.signals.balance" class="signal-pill" :class="'pill-' + e.signals.balanceRank">{{ e.signals.balance }}</span>
                        </div>
                      </td>
                      <td>
                        <div v-for="e in r.entrants" :key="e.dancerId" class="entrant-line">
                          <span v-if="e.signals.combo" class="signal-pill" :class="'pill-' + e.signals.comboRank">{{ e.signals.combo }}</span>
                        </div>
                      </td>
                      <td>
                        <div v-for="e in r.entrants" :key="e.dancerId" class="entrant-line">
                          <span v-if="e.signals.prevGold" class="signal-pill pill-gold" :title="e.signals.prevGold">🥇</span>
                          <span v-else class="muted">—</span>
                        </div>
                      </td>
                      <td>
                        <select
                          class="place-select"
                          :value="r.placement"
                          :disabled="!r.pieceId || resultSavingKey === r.rowKey"
                          @change="onPlaceChange(r, $event)"
                        >
                          <option v-for="opt in placeOptions" :key="opt.value" :value="opt.value">{{ opt.text }}</option>
                        </select>
                      </td>
                      <td>
                        <div class="score-cell">
                          <input
                            class="score-input"
                            type="text"
                            inputmode="decimal"
                            placeholder="—"
                            :value="r.score"
                            :disabled="!r.pieceId || resultSavingKey === r.rowKey"
                            @change="onScoreChange(r, $event)"
                          />
                          <span v-if="resultSavingKey === r.rowKey" class="result-status">…</span>
                          <span v-else-if="resultSavedKey === r.rowKey" class="result-status is-saved">✓</span>
                        </div>
                      </td>
                      <td>
                        <v-btn x-small outlined @click="openReview(r)">
                          {{ r.pieceId ? 'Review' : 'Pick Piece' }}
                        </v-btn>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <!-- Engravings Dialog -->
            <v-dialog v-model="showEngravingsDialog" max-width="900" scrollable>
              <v-card>
                <v-card-title>
                  Medal Engravings to Order
                  <v-spacer />
                  <span class="muted">{{ engravingMedals.length }} medals across {{ engravingDivisionCount }} divisions</span>
                </v-card-title>
                <v-card-text style="max-height: 75vh; overflow-y: auto;">

                  <div class="engraving-summary mb-3">
                    <div class="engraving-stat" v-for="b in engravingBreakdown" :key="b.label">
                      <span class="stat-num">{{ b.count }}</span>
                      <span class="stat-label">{{ b.label }}</span>
                    </div>
                  </div>

                  <p v-if="engravingsPendingNote" class="warn-row mb-3">{{ engravingsPendingNote }}</p>

                  <div v-if="engravingMedals.length === 0" class="muted">No divisions assigned yet — assign divisions to dancers first.</div>

                  <div v-if="engravingMedals.length" class="engraving-list">
                    <div v-for="m in engravingMedals" :key="m.plate" class="engraving-row">
                      <div class="engraving-plate">#{{ m.plate }}</div>
                      <pre class="engraving-text">{{ m.line1 }}
{{ m.line2 }}
{{ m.line3 }}</pre>
                      <button
                        type="button"
                        class="engraving-copy-btn"
                        :class="{ 'is-flashed': copiedPlate === m.plate }"
                        @click="copyOneMedal(m)"
                      >{{ copiedPlate === m.plate ? '✓ Copied' : 'Copy' }}</button>
                    </div>
                  </div>

                </v-card-text>
                <v-card-actions>
                  <v-btn text @click="showEngravingsDialog = false">Close</v-btn>
                  <v-spacer />
                  <v-btn outlined :disabled="engravingMedals.length === 0" @click="downloadEngravingsCsv">↓ CSV</v-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>

            <!-- Review Dialog -->
            <v-dialog v-model="showReviewDialog" max-width="780" scrollable>
              <v-card v-if="reviewRow">
                <v-card-title>
                  {{ reviewRow.pieceTitle }}
                  <span v-if="reviewRow.entrants.length > 1" class="group-tag ml-2">Group entry · {{ reviewRow.entrants.length }} dancers</span>
                </v-card-title>
                <v-card-subtitle>
                  <div>{{ reviewRow.entrants.map(e => e.dancerName).join(' + ') }}</div>
                  <div v-if="selectedPiece" class="muted">
                    {{ selectedPiece.songArtist }} ({{ selectedPiece.pieceType }} · {{ selectedPiece.groupSize }})
                  </div>
                  <div v-else-if="!reviewRow.pieceId" class="muted">No competition piece selected yet.</div>
                </v-card-subtitle>
                <v-card-text style="max-height: 70vh; overflow-y: auto;">

                  <!-- Piece picker — for orphans, OR when admin clicks "Change piece" on a solo. -->
                  <div v-if="!reviewRow.pieceId || changingPiece">
                    <div class="form-section-label">
                      Competition Piece
                      <v-btn
                        v-if="changingPiece && reviewRow.pieceId"
                        x-small
                        text
                        @click="cancelChangePiece"
                      >Cancel change</v-btn>
                    </div>
                    <v-select
                      v-if="dancerPieceOptions.length"
                      v-model="pickedPieceId"
                      :items="dancerPieceOptions"
                      label="Choose piece"
                      dense
                      hide-details
                    />
                    <div v-else class="muted">This dancer has no registered showcase pieces yet.</div>
                    <div v-if="reviewRow.pieceId && changingPiece && pickedPieceId !== reviewRow.pieceId" class="change-piece-warning mt-2">
                      ⚠ Changing the piece will clear the self-assessment survey and reset the division — both will need to be redone for the new piece.
                    </div>
                  </div>

                  <!-- Solos: offer a "Change piece" action when a piece is already picked. -->
                  <div v-if="reviewRow.pieceId && !changingPiece && reviewRow.entrants.length === 1" class="change-piece-row">
                    <v-btn x-small outlined @click="startChangePiece">↻ Change piece</v-btn>
                    <v-btn x-small outlined color="error" class="ml-2" :loading="resetting" @click="resetSurveyAndDivision">Clear survey &amp; reset division</v-btn>
                  </div>

                  <!-- Assignment -->
                  <div class="form-section-label mt-4">Assigned Division</div>
                  <div class="assignment-grid">
                    <v-select
                      v-model="assignStyle"
                      :items="styleOptions"
                      label="Style"
                      dense
                    />
                    <v-select
                      v-model="assignLevel"
                      :items="levelOptions"
                      label="Skill Level"
                      dense
                      :disabled="levelDisabled"
                      :hint="levelDisabled ? 'Not used for this division' : ''"
                      persistent-hint
                    />
                  </div>
                  <div class="preview-row">
                    <span class="preview-label">Will save as:</span>
                    <v-chip v-if="combinedDivision !== 'None'" small :color="divisionColor(combinedDivision)" text-color="white">
                      {{ combinedDivision }}
                    </v-chip>
                    <span v-else class="muted">None</span>
                  </div>

                  <!-- Recommendations: one per entrant -->
                  <div v-if="reviewEntrantRecommendations.length" class="recommendation-box">
                    <div class="recommendation-header">
                      <v-icon small color="#5d4037">mdi-lightbulb-on-outline</v-icon>
                      Recommended division{{ reviewEntrantRecommendations.length > 1 ? 's' : '' }}
                    </div>
                    <div v-for="(item, i) in reviewEntrantRecommendations" :key="i" class="recommendation-entrant">
                      <div class="recommendation-entrant-head">
                        <strong v-if="reviewRow.entrants.length > 1">{{ item.dancerName }}: </strong>{{ item.rec.division }}
                        <v-btn x-small outlined class="ml-2" @click="applyRecommendationFor(item.rec)">Apply</v-btn>
                      </div>
                      <ul class="recommendation-reasons">
                        <li v-for="(reason, j) in item.rec.reasons" :key="j">{{ reason }}</li>
                      </ul>
                    </div>
                  </div>

                  <!-- Surveys: one block per entrant -->
                  <div class="form-section-label mt-4">Self-Assessment Survey{{ reviewRow.entrants.length > 1 ? 's' : '' }}</div>
                  <div v-for="e in reviewRow.entrants" :key="e.dancerId" class="entrant-survey">
                    <div v-if="reviewRow.entrants.length > 1" class="entrant-survey-head">{{ e.dancerName }}</div>
                    <div v-if="!e.survey" class="muted">(No survey on file.)</div>
                    <table v-else class="survey-table">
                      <tbody>
                        <tr v-for="q in surveyQuestionList.filter(q => e.survey[q.key] !== undefined && e.survey[q.key] !== '')" :key="q.key">
                          <td class="survey-q">{{ q.label }}</td>
                          <td class="survey-a">{{ e.survey[q.key] || '—' }}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  <div v-if="saveError" class="error--text mt-2">{{ saveError }}</div>

                </v-card-text>
                <v-card-actions>
                  <v-btn text @click="closeReview">Cancel</v-btn>
                  <v-btn
                    v-if="nextUnassignedRow"
                    text
                    :disabled="saving"
                    @click="skipToNext"
                  >Skip →</v-btn>
                  <v-spacer />
                  <span v-if="nextUnassignedRow" class="muted mr-3">{{ pendingRemainingCount }} pending</span>
                  <v-btn
                    color="primary"
                    :loading="saving"
                    :disabled="!pickedPieceId"
                    @click="saveAssignment(false)"
                  >Save Division</v-btn>
                  <v-btn
                    v-if="nextUnassignedRow"
                    color="primary"
                    class="ml-2"
                    :loading="saving"
                    :disabled="!pickedPieceId"
                    @click="saveAssignment(true)"
                  >Save &amp; Next →</v-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>

          </v-container>
        </v-card-text>
      </v-flex>
    </v-layout>
  </v-container>
</template>

<script>
import { buildLineupCodeMap } from '@/utils/showcase-order'
import iabfAdminMixin from '@/utils/iabf-admin-mixin'
import {
  COMPETITION_PLACE_OPTIONS,
  canonicalCompetitionPlacement,
  competitionScoreFromResult,
  matchStoredResultToCompetitionRow,
  matchSpecialAwardToRow,
  specialAwardIdFromName,
  specialAwardsFromImportedResults
} from '@/utils/iabf-festival-history'

const STYLE_OPTIONS = [
  'Classical Variation',
  'Original Ballet Choreography',
  'Contemporary/Lyrical',
  'Groups'
]

// Styles that don't take a skill level (one division for everyone).
const LEVELLESS_STYLES = new Set(['Groups'])

const LEVEL_OPTIONS = [
  'Beginner',
  'Intermediate',
  'Advanced',
  'Professional',
  'Beginner Pointe',
  'Intermediate/Advanced Pointe',
  'Professional Pointe'
]

// Maps the dancer's self-reported survey.style → admin-side division style.
const STYLE_MAP = {
  'Classical Variation': 'Classical Variation',
  'My own Ballet Choreography': 'Original Ballet Choreography',
  'My own Contemporary/Lyrical': 'Contemporary/Lyrical'
}

const SURVEY_QUESTIONS = [
  { key: 'style', label: 'Style of piece' },
  { key: 'shoeType', label: 'Shoes' },
  { key: 'performanceFormat', label: 'Performance format' },
  { key: 'pointeTrainingDuration', label: 'Pointe training duration' },
  { key: 'balletJourneyStage', label: 'Ballet journey' },
  { key: 'performanceExperience', label: 'Performance experience' },
  { key: 'balanceAndControl', label: 'Balance & control' },
  { key: 'turningAbility', label: 'Turning ability' },
  { key: 'footworkExperience', label: 'Footwork' },
  { key: 'musicalityExpression', label: 'Musicality & expression' },
  { key: 'combinationExecution', label: 'Combination execution' },
  { key: 'challengingMoments', label: 'Most challenging movements' },
  { key: 'joyfulMoments', label: 'Most joyful movements' },
  { key: 'artisticRisks', label: 'Artistic risks' },
  { key: 'performanceGoal', label: 'Goal from competition' },
  { key: 'previousGold', label: 'Previous IABF gold medals' }
]

// True when the dancer's "previous gold" answer actually claims a gold.
// We treat any non-negation as a claim, since the question itself asks
// "Have you won Gold at former IABF's?" and people commonly answer by
// listing their wins (e.g. "Advanced Solo Variation - 2022, 2023, 2024")
// without ever using the word "gold".
function isPreviousGoldClaim (text) {
  const t = (text || '').trim()
  if (t.length < 3) return false
  const lower = t.toLowerCase()
  if (/^(no\b|nope|none|nah|never|not\b|n\/a|ni\b|haven|hasn|first time|first iabf)/i.test(lower)) return false
  return true
}

function recommendDivision (survey) {
  if (!survey) return null
  const reasons = []
  // Style
  const style = STYLE_MAP[survey.style] || null
  if (style) reasons.push(`Self-reported style: ${survey.style} → ${style}`)

  // Level
  const isPointe = survey.shoeType === 'En Pointe'
  let baseLevel = null
  if (isPointe) {
    const pt = survey.pointeTrainingDuration || ''
    if (pt.startsWith('Less than 1 year')) {
      baseLevel = 'Beginner Pointe'
    } else if (pt.startsWith('1-3 years')) {
      baseLevel = 'Beginner Pointe'
    } else if (pt.startsWith('3+ years')) {
      baseLevel = 'Intermediate/Advanced Pointe'
    } else if (pt === 'Professional') {
      baseLevel = 'Professional Pointe'
    } else {
      baseLevel = 'Beginner Pointe'
    }
    if (pt) reasons.push(`Pointe training: ${pt}`)
  } else {
    const j = survey.balletJourneyStage || ''
    if (j.startsWith('Beginner')) baseLevel = 'Beginner'
    else if (j.startsWith('Intermediate')) baseLevel = 'Intermediate'
    else if (j.startsWith('Advanced')) baseLevel = 'Advanced'
    else if (j.startsWith('Professional')) baseLevel = 'Professional'
    else baseLevel = 'Beginner'
    if (j) reasons.push(`Ballet journey: ${j}`)
  }

  // Technical-skill nudges (use combinationExecution + turningAbility as secondary signal)
  const strongSignals = [
    survey.combinationExecution === 'With ease and artistic flair',
    survey.turningAbility === 'Triples or more with control',
    survey.turningAbility === 'Comfortable with variety of turns',
    survey.musicalityExpression === 'Artistry and musicality central, use dynamics and phrasing intentionally'
  ].filter(Boolean).length
  const weakSignals = [
    survey.combinationExecution === "Wouldn't be able to do this yet",
    survey.turningAbility === 'Still learning to spot and complete single pirouette',
    survey.balanceAndControl === 'Still working on holding balances, need barre for stability'
  ].filter(Boolean).length

  let levelNudge = ''
  if (strongSignals >= 2) {
    levelNudge = `${strongSignals} strong technical signals — consider bumping up`
    reasons.push(levelNudge)
  } else if (weakSignals >= 2) {
    levelNudge = `${weakSignals} foundational signals — consider keeping at base level`
    reasons.push(levelNudge)
  }

  // Previous gold → suggest bumping up (only when text actually claims a gold)
  const prevText = (survey.previousGold || '').trim()
  if (isPreviousGoldClaim(prevText)) {
    reasons.push(`Previous gold: "${prevText}" — consider level advancement`)
  }

  if (!style || !baseLevel) return null
  return { division: `${style} - ${baseLevel}`, style, level: baseLevel, reasons }
}

function extractSignals (survey) {
  if (!survey) return {
    journey: '', journeyRank: 0, shoes: '', isPointe: false,
    turn: '', turnRank: 0, balance: '', balanceRank: 0,
    combo: '', comboRank: 0, prevGold: ''
  }
  const journeyMap = [
    ['Beginner', 'Beg', 1],
    ['Intermediate', 'Int', 2],
    ['Advanced', 'Adv', 3],
    ['Professional', 'Pro', 4]
  ]
  const j = (survey.balletJourneyStage || '').toString()
  const journey = journeyMap.find(m => j.startsWith(m[0])) || ['', '', 0]
  const isPointe = survey.shoeType === 'En Pointe'
  const shoes = isPointe
    ? (survey.pointeTrainingDuration || 'Pointe')
    : (survey.shoeType === 'Flat Shoes/Barefoot' ? 'Flat' : '')

  // Turning: 5 levels (still learning → variety of turns), plus "not part of piece"
  const turnAnswer = survey.turningAbility || ''
  let turn = '', turnRank = 0
  if (turnAnswer.startsWith('Still')) { turn = 'Single (learning)'; turnRank = 1 }
  else if (turnAnswer.startsWith('Clean single')) { turn = 'Single'; turnRank = 2 }
  else if (turnAnswer.startsWith('Consistently')) { turn = 'Double'; turnRank = 3 }
  else if (turnAnswer.startsWith('Triples')) { turn = 'Triple+'; turnRank = 4 }
  else if (turnAnswer.startsWith('Comfortable')) { turn = 'Variety'; turnRank = 4 }
  else if (turnAnswer.startsWith('Turning is not')) { turn = 'N/A'; turnRank = 0 }

  // Balance: 4 levels
  const balanceAnswer = survey.balanceAndControl || ''
  const balanceLevels = [
    'Still working',
    'Can hold balances briefly',
    'Balance confidently',
    'Strong reliable balance'
  ]
  const balanceShort = ['Wobbly', 'Brief', 'Confident', 'Strong']
  let balance = '', balanceRank = 0
  for (let i = 0; i < balanceLevels.length; i++) {
    if (balanceAnswer.startsWith(balanceLevels[i])) {
      balance = balanceShort[i]
      balanceRank = i + 1
      break
    }
  }

  // Combination execution: 5 levels
  const comboAnswer = survey.combinationExecution || ''
  const comboLevels = [
    "Wouldn't",
    'Could attempt',
    'Moderate',
    'Smoothly',
    'With ease'
  ]
  const comboShort = ['Not yet', 'Slow', 'Moderate', 'Smooth', 'Easy']
  let combo = '', comboRank = 0
  for (let i = 0; i < comboLevels.length; i++) {
    if (comboAnswer.startsWith(comboLevels[i])) {
      combo = comboShort[i]
      comboRank = i + 1
      break
    }
  }

  const prevGoldText = (survey.previousGold || '').trim()
  const prevGold = isPreviousGoldClaim(prevGoldText) ? prevGoldText : ''

  return {
    journey: journey[1],
    journeyRank: journey[2],
    shoes,
    isPointe,
    turn,
    turnRank,
    balance,
    balanceRank,
    combo,
    comboRank,
    prevGold
  }
}

function parseExistingDivision (division) {
  if (!division || division === 'None') return { style: '', level: '' }
  // Format: "{Style} - {Level}"
  const idx = division.indexOf(' - ')
  if (idx === -1) return { style: division, level: '' }
  return { style: division.slice(0, idx), level: division.slice(idx + 3) }
}

export default {
  mixins: [iabfAdminMixin],
  data () {
    return {
      loading: true,
      rows: [],
      styleOptions: STYLE_OPTIONS,
      levelOptions: LEVEL_OPTIONS,
      showReviewDialog: false,
      showEngravingsDialog: false,
      // Toggle between size-based medal allocation (Gold-only ≤2, +Silver
      // at ≥3, +Bronze at ≥5) and a flat 1·2·3 podium for every division
      // with ≥3 competitors. Persists for the session only.
      useFullMedals: false,
      copiedPlate: null,
      reviewRow: null,
      pickedPieceId: null,
      assignStyle: '',
      assignLevel: '',
      saving: false,
      saveError: '',
      changingPiece: false,
      resetting: false,
      showScorecardPdfDialog: false,
      buildingScorecardPdf: false,
      scorecardPdfError: '',
      judgeKeys: ['judge1', 'judge2', 'judge3'],
      templates: {
        judge1: { classical: null, contemporary: null, original: null },
        judge2: { classical: null, contemporary: null, original: null },
        judge3: { classical: null, contemporary: null, original: null }
      },
      // Placement of the typed-in dancer name on the template (coords
      // are PDF points from the bottom-left). Default approximately
      // hits the "Dancer:" label on the existing IABF templates;
      // tweak via the dialog and the value persists in localStorage.
      slot: {
        dancerX: 215,
        dancerY: 220,
        dancerSize: 16
      },
      yearResults: [],
      resultSavingKey: '',
      resultSavedKey: '',
      resultError: '',
      importFillNote: '',
      specialAwards: [],
      newAwardName: '',
      jcSaving: false,
      jcNote: ''
    }
  },
  computed: {
    rowsWithDivision () {
      return this.rows.filter(r => r.division && r.division !== 'None')
    },
    rowsWithResult () {
      return this.rows.filter(r => r.placement)
    },
    placeOptions () {
      return COMPETITION_PLACE_OPTIONS
    },
    specialAwardsAssignedCount () {
      return this.specialAwards.filter(a => a.pieceId).length
    },
    jcPieceOptions () {
      return this.rows
        .filter(r => r.pieceId)
        .map(r => {
          const names = (r.entrants || []).map(e => e.dancerName).filter(Boolean).join(' + ')
          const div = (r.division && r.division !== 'None') ? r.division : ''
          const unique = names
            ? (names + ' — ' + r.pieceTitle + (div ? ' (' + div + ')' : ''))
            : (r.pieceTitle + (div ? ' (' + div + ')' : ''))
          return { pieceId: r.pieceId, label: unique }
        })
        .sort((a, b) => a.label.localeCompare(b.label))
    },
    rowsMissingMusic () {
      return this.rows.filter(r => !r.musicReceived)
    },
    totalDancersCount () {
      return this.rows.reduce((sum, r) => sum + r.entrants.length, 0)
    },
    byDivision () {
      const counts = {}
      for (const r of this.rows) {
        const key = (r.division && r.division !== 'None') ? r.division : 'Division Pending'
        counts[key] = (counts[key] || 0) + 1
      }
      return Object.keys(counts)
        .sort((a, b) => a.localeCompare(b))
        .map(name => ({ name, count: counts[name] }))
    },
    skippedCompetitors () {
      // Rows that won't get a scorecard: no division assigned
      // (orphan registration with no piece, OR piece without competition
      // field set). Flatten per-entrant so the admin sees each missing
      // dancer by name.
      const out = []
      for (const r of this.rows) {
        if (r.division && r.division !== 'None') continue
        for (const e of r.entrants) {
          out.push({
            dancerName: e.dancerName,
            pieceTitle: r.pieceTitle,
            reason: r.pieceId ? 'Piece has no division set' : 'Not attached to a piece'
          })
        }
      }
      return out.sort((a, b) => a.dancerName.localeCompare(b.dancerName))
    },
    pdfCompetitorNames () {
      // Every dancer who WILL get a scorecard, alphabetized. Useful for
      // verifying that a specific name made it in.
      const names = new Set()
      for (const r of this.rows) {
        if (!r.division || r.division === 'None') continue
        for (const e of r.entrants) {
          if (e.dancerName) names.add(e.dancerName)
        }
      }
      return Array.from(names).sort((a, b) => a.localeCompare(b))
    },
    invisibleCompetitors () {
      // Dancers who exist in the dancer list but appear nowhere — neither
      // in a printable row (any division) nor in the skipped list. This
      // catches the case where someone is in a piece.competitionEntrants
      // array but the piece itself doesn't have competitionEntry: true,
      // OR they're competition-registered but not yet wired to any piece
      // AND not surfaced as an orphan due to some data gap.
      const seen = new Set()
      for (const r of this.rows) {
        for (const e of r.entrants) {
          if (e.dancerId) seen.add(e.dancerId)
        }
      }
      const out = []
      for (const d of this.$store.getters.loadedIABFDancers) {
        if (seen.has(d.id)) continue
        // Only flag dancers who LOOK like competitors — anyone with
        // any competition-related field set.
        const looksLikeCompetitor = d.competitionRegistered ||
          d.competitionPieceId ||
          d.competitionSurvey
        if (!looksLikeCompetitor) continue
        const reasons = []
        if (!d.competitionRegistered) reasons.push('competitionRegistered = false')
        if (!d.competitionPieceId) reasons.push('no competitionPieceId')
        out.push({
          dancerName: `${d.firstName || ''} ${d.lastName || ''}`.trim() || '(unnamed)',
          dancerId: d.id,
          reasons: reasons.join(', ') || 'in a piece but piece is not flagged competitionEntry'
        })
      }
      return out.sort((a, b) => a.dancerName.localeCompare(b.dancerName))
    },
    expectedPdfPageCount () {
      // Pairs walk straight through the lineup-sorted rows: each pair
      // produces 1 front + 1 back = 2 pages, × 3 judges. Plus 3 extras
      // sheets per judge (one per template) with blank backs = 6 extra
      // pages per judge. Templates can differ across the two halves of
      // a sheet (each half draws its own template), so no per-template
      // ceil is needed.
      const printable = this.rows.filter(r => r.division && r.division !== 'None').length
      const judgePages = Math.ceil(printable / 2) * 2 + 6
      return judgePages * 3
    },
    canBuildScorecardPdf () {
      for (const judge of this.judgeKeys) {
        const set = this.templates[judge]
        if (!set.classical || !set.contemporary || !set.original) return false
      }
      return true
    },
    divisionGroups () {
      const groups = {}
      for (const r of this.rows) {
        const key = (r.division && r.division !== 'None') ? r.division : 'Division Pending'
        if (!groups[key]) groups[key] = []
        groups[key].push(r)
      }
      const LEVEL_RANK = {
        'Beginner': 1,
        'Intermediate': 2,
        'Advanced': 3,
        'Professional': 4,
        'Beginner Pointe': 5,
        'Intermediate/Advanced Pointe': 6,
        'Professional Pointe': 7
      }
      const parse = name => {
        const idx = name.indexOf(' - ')
        if (idx === -1) return { style: name, level: '', rank: 99 }
        const style = name.slice(0, idx)
        const level = name.slice(idx + 3)
        return { style, level, rank: LEVEL_RANK[level] || 99 }
      }
      // Sort: Pending first, then by style alphabetically, then by level rank (Beg→Int→Adv→Pro→…Pointe).
      const names = Object.keys(groups).sort((a, b) => {
        if (a === 'Division Pending') return -1
        if (b === 'Division Pending') return 1
        const pa = parse(a)
        const pb = parse(b)
        if (pa.style !== pb.style) return pa.style.localeCompare(pb.style)
        if (pa.rank !== pb.rank) return pa.rank - pb.rank
        return pa.level.localeCompare(pb.level)
      })
      return names.map(name => ({
        name,
        competitors: groups[name].slice().sort((a, b) => {
          const an = a.entrants[0] ? a.entrants[0].dancerName : ''
          const bn = b.entrants[0] ? b.entrants[0].dancerName : ''
          return an.localeCompare(bn)
        })
      }))
    },
    combinedDivision () {
      if (!this.assignStyle) return 'None'
      if (LEVELLESS_STYLES.has(this.assignStyle)) return this.assignStyle
      if (!this.assignLevel) return 'None'
      return `${this.assignStyle} - ${this.assignLevel}`
    },
    levelDisabled () {
      return LEVELLESS_STYLES.has(this.assignStyle)
    },
    allPieces () {
      return this.$store.getters.loadedIABFPieces || []
    },
    dancerPieceOptions () {
      // Shown for orphan rows AND when the admin clicks "Change piece" on
      // an already-assigned solo. List every showcase piece the single
      // dancer is registered on. Also include any currently-attached
      // competition piece even if it dropped out of `dancers`, so the
      // active selection is visible in the dropdown.
      if (!this.reviewRow) return []
      const dancerId = this.reviewRow.entrants[0] && this.reviewRow.entrants[0].dancerId
      if (!dancerId) return []
      const matches = this.allPieces.filter(p => {
        if (Array.isArray(p.dancers) && p.dancers.includes(dancerId)) return true
        if (Array.isArray(p.competitionEntrants) && p.competitionEntrants.includes(dancerId)) return true
        if (Array.isArray(p.namedDancersIds) && p.namedDancersIds.includes(dancerId)) return true
        return false
      })
      return matches.map(p => ({
        text: `${p.title || '(untitled)'} — ${p.songArtist || ''} (${p.pieceType || '?'})`,
        value: p.id
      }))
    },
    selectedPiece () {
      if (!this.pickedPieceId) return null
      return this.allPieces.find(p => p.id === this.pickedPieceId) || null
    },
    engravingGroups () {
      // One group per assigned division, sorted by the same key the division view uses.
      const PLACE_TO_LINE = { Gold: '1ST PLACE', Silver: '2ND PLACE', Bronze: '3RD PLACE' }
      const STYLE_ABBREV = {
        'Classical Variation': 'CLASSICAL VARIATION',
        'Original Ballet Choreography': 'NEW BALLET CHOREO',
        'Contemporary/Lyrical': 'CONTEMPORARY',
        'Groups': 'GROUPS'
      }
      const LEVEL_ABBREV = {
        'Beginner': 'BEG',
        'Intermediate': 'INT',
        'Advanced': 'ADV',
        'Professional': 'PRO',
        'Beginner Pointe': 'BEG POINTE',
        'Intermediate/Advanced Pointe': 'INT/ADV POINTE',
        'Professional Pointe': 'PRO POINTE'
      }
      const splitDivision = name => {
        const idx = name.indexOf(' - ')
        if (idx === -1) return { style: name, level: '' }
        return { style: name.slice(0, idx), level: name.slice(idx + 3) }
      }
      const assigned = this.divisionGroups.filter(g => g.name !== 'Division Pending')
      return assigned.map(g => {
        const places = this.medalPlacesFor(g.competitors.length)
        const { style, level } = splitDivision(g.name)
        const styleLine = STYLE_ABBREV[style] || style.toUpperCase()
        const levelAbbrev = level ? (LEVEL_ABBREV[level] || level.toUpperCase()) : ''
        return {
          division: g.name,
          competitorCount: g.competitors.length,
          medals: places.map(place => {
            const placeText = PLACE_TO_LINE[place] || place.toUpperCase()
            return {
              place,
              // Line 1: "1ST PLACE - BEG" (or just "1ST PLACE" when the
              // division has no level — e.g. Groups).
              line1: levelAbbrev ? `${placeText} - ${levelAbbrev}` : placeText,
              line2: styleLine,
              line3: `IABF ${this.adminFestivalYear}`
            }
          })
        }
      })
    },
    engravingMedals () {
      // Flatten with sequential plate numbers across all divisions.
      const out = []
      let plate = 0
      for (const g of this.engravingGroups) {
        for (const m of g.medals) {
          plate += 1
          out.push({ plate, division: g.division, ...m })
        }
      }
      return out
    },
    engravingDivisionCount () {
      return this.engravingGroups.length
    },
    engravingBreakdown () {
      const counts = { Gold: 0, Silver: 0, Bronze: 0 }
      for (const m of this.engravingMedals) counts[m.place] = (counts[m.place] || 0) + 1
      return [
        { label: 'Gold', count: counts.Gold },
        { label: 'Silver', count: counts.Silver },
        { label: 'Bronze', count: counts.Bronze },
        { label: 'Total', count: this.engravingMedals.length }
      ]
    },
    engravingsPendingNote () {
      const pendingGroup = this.divisionGroups.find(g => g.name === 'Division Pending')
      if (!pendingGroup) return ''
      const n = pendingGroup.competitors.length
      return `${n} ${n === 1 ? 'entry is' : 'entries are'} still pending division assignment — their medals aren't counted here yet.`
    },
    isUnassigned () {
      return row => !row.division || row.division === 'None'
    },
    nextUnassignedRow () {
      if (!this.reviewRow) return null
      const idx = this.rows.findIndex(r => r.rowKey === this.reviewRow.rowKey)
      const search = [
        ...this.rows.slice(idx + 1),
        ...this.rows.slice(0, Math.max(idx, 0))
      ]
      return search.find(r => this.isUnassigned(r) && r.pieceId) || null
    },
    pendingRemainingCount () {
      if (!this.reviewRow) return 0
      return this.rows.filter(r =>
        this.isUnassigned(r) && r.rowKey !== this.reviewRow.rowKey
      ).length
    },
    surveyQuestionList () {
      return SURVEY_QUESTIONS
    },
    reviewEntrantRecommendations () {
      if (!this.reviewRow) return []
      return this.reviewRow.entrants
        .map(e => ({ dancerName: e.dancerName, rec: recommendDivision(e.survey) }))
        .filter(x => x.rec)
    }
  },
  async created () {
    try {
      const saved = window.localStorage.getItem('iabf.scorecardSlot.v1')
      if (saved) {
        const parsed = JSON.parse(saved)
        if (parsed && typeof parsed === 'object') {
          this.slot = Object.assign({}, this.slot, parsed)
        }
      }
    } catch (e) {
      // ignore parse / storage errors, defaults stand
    }
    await Promise.all([
      this.$store.dispatch('loadIABFDancers'),
      this.$store.dispatch('loadIABFPieces', { useAdminYear: true })
    ])
    try {
      const yearDoc = await this.$store.dispatch(
        'loadCompetitionYearDoc',
        this.adminFestivalYear
      )
      this.yearResults = (yearDoc && yearDoc.results) || []
      this.specialAwards = (yearDoc && yearDoc.specialAwards) || []
    } catch (e) {
      this.yearResults = []
      this.specialAwards = []
    }
    this.rebuildRows()
    await this.applyImportedResults()
    await this.hydrateSpecialAwards()
    this.loading = false
  },
  methods: {
    openScorecardPdf () {
      this.scorecardPdfError = ''
      this.showScorecardPdfDialog = true
    },
    async onTemplateChosen (judge, division, event) {
      const file = event.target.files && event.target.files[0]
      if (!file) return
      try {
        const bytes = await file.arrayBuffer()
        this.$set(this.templates[judge], division, { fileName: file.name, bytes: new Uint8Array(bytes) })
      } catch (e) {
        console.error('template load failed:', e)
        this.scorecardPdfError = 'Could not read that file.'
      }
    },
    saveSlot () {
      try {
        window.localStorage.setItem('iabf.scorecardSlot.v1', JSON.stringify(this.slot))
      } catch (e) {
        // localStorage disabled / quota — fine, just won't persist
      }
    },
    async buildScorecardPdf (previewOnly) {
      // The dialog's main "Build" button binds @click="buildScorecardPdf"
      // (no parens) so Vue hands us the MouseEvent; only the explicit
      // preview button passes a boolean true.
      const preview = previewOnly === true
      if (!this.canBuildScorecardPdf) return
      this.buildingScorecardPdf = true
      this.scorecardPdfError = ''
      try {
        const { PDFDocument, rgb } = await import('pdf-lib')
        const fontkit = (await import('@pdf-lib/fontkit')).default
        // Load all 9 templates: 3 divisions × 3 judges.
        const tplsByJudge = {}
        for (const judge of this.judgeKeys) {
          tplsByJudge[judge] = {
            classical: await PDFDocument.load(this.templates[judge].classical.bytes),
            contemporary: await PDFDocument.load(this.templates[judge].contemporary.bytes),
            original: await PDFDocument.load(this.templates[judge].original.bytes)
          }
        }
        const out = await PDFDocument.create()
        out.registerFontkit(fontkit)
        // Montserrat — bundled under /public/fonts/ so the bytes are
        // served from this app's own origin (no CORS / no CDN).
        const fetchFont = async (path) => {
          const res = await fetch(path)
          if (!res.ok) throw new Error(`Failed to load ${path}: ${res.status}`)
          return new Uint8Array(await res.arrayBuffer())
        }
        const [regBytes, boldBytes, italicBytes] = await Promise.all([
          fetchFont('/fonts/Montserrat-Regular.ttf'),
          fetchFont('/fonts/Montserrat-Bold.ttf'),
          fetchFont('/fonts/Montserrat-Italic.ttf')
        ])
        const fontReg = await out.embedFont(regBytes, { subset: true })
        const fontBold = await out.embedFont(boldBytes, { subset: true })
        const fontItalic = await out.embedFont(italicBytes, { subset: true })

        // Montserrat covers Latin Extended so accented characters work
        // as-is. Still drop emoji (no glyphs) and normalize a few
        // oddballs leftover from the prior Helvetica logic.
        const sanitize = (text) => {
          return String(text || '')
            .replace(/[‘’‚‛]/g, "'")
            .replace(/[“”„‟]/g, '"')
            .replace(/[–—−]/g, '-')
            .replace(/…/g, '...')
            .replace(/ /g, ' ')
            // High surrogate pairs (emoji) — drop the pair as one unit.
            .replace(/[\uD800-\uDBFF][\uDC00-\uDFFF]/g, '')
        }

        // Wrap text into lines that fit a max width. Split on \n first
        // so output lines never contain embedded newlines — pdf-lib's
        // drawText auto-renders \n as extra lines with its own spacing,
        // which would collide with our own y decrement.
        const wrap = (text, font, size, maxWidth) => {
          const result = []
          const paragraphs = String(text || '').replace(/\r\n/g, '\n').split('\n')
          for (const para of paragraphs) {
            const words = para.split(/\s+/).filter(Boolean)
            if (words.length === 0) continue
            let current = ''
            for (const word of words) {
              const probe = current ? current + ' ' + word : word
              if (font.widthOfTextAtSize(probe, size) > maxWidth && current) {
                result.push(current)
                current = word
              } else {
                current = probe
              }
            }
            if (current) result.push(current)
          }
          return result
        }

        // pieceId → "B-10"-style code (block letter + 1-indexed slot
        // within that block). Rows without a pieceId or whose piece
        // isn't placed in a block won't have a code.
        const lineupCodes = buildLineupCodeMap(this.$store.getters.loadedIABFPieces || [])

        // Sort rows by showcase lineup (Block AA, A, B, C, D and position
        // within each block) so judges can flip through scorecards in
        // performance order. Rows without a lineup code (piece not placed
        // in a block yet) fall to the end in piece-title order.
        const BLOCK_RANK = { AA: 0, A: 1, B: 2, C: 3, D: 4 }
        const lineupSortKey = (row) => {
          const code = row.pieceId ? lineupCodes.get(row.pieceId) : null
          if (!code) return [99, 0]
          const m = code.match(/^([A-Z]+)-(\d+)$/)
          if (!m) return [99, 0]
          const rank = BLOCK_RANK[m[1]] !== undefined ? BLOCK_RANK[m[1]] : 98
          return [rank, parseInt(m[2], 10)]
        }
        const printableRows = this.rows
          .filter(r => r.division && r.division !== 'None')
          .slice()
          .sort((a, b) => {
            const [aR, aP] = lineupSortKey(a)
            const [bR, bP] = lineupSortKey(b)
            if (aR !== bR) return aR - bR
            if (aP !== bP) return aP - bP
            return (a.pieceTitle || '').localeCompare(b.pieceTitle || '')
          })

        if (!printableRows.length) {
          this.scorecardPdfError = 'No competition entries with an assigned division yet.'
          this.buildingScorecardPdf = false
          return
        }

        // Preview = first pair so the user sees both halves of a sheet.
        const rowsToProcess = preview ? printableRows.slice(0, 2) : printableRows

        // The templates are landscape 792x612 with TWO identical
        // scorecards already laid out side-by-side (left half + right
        // half). Each half is 396 wide. slot.dancerX/Y are positioned
        // within the LEFT half; add 396 to land in the RIGHT half.
        const pageW = 792
        const pageH = 612
        const halfW = pageW / 2

        // Abbreviate long division labels so they fit on one line in
        // both the front italic and the back header.
        const formatDivision = (division) => {
          return String(division || '').replace(/Intermediate\/Advanced/g, 'Int/Adv')
        }

        // Teacher / coach / choreographer credit shown under the
        // division on the back. Mirrors teacherCreditFor() in
        // IABFPieces.vue: respect an explicit prefix if the admin
        // wrote one; otherwise "coached by" for Classical Variation,
        // "choreographed by" for everything else.
        const teacherCreditFor = (row) => {
          const t = String(row.programTeacher || '').trim()
          if (!t) return ''
          if (/(coached by|choreographed by|taught by|teacher\b|coach\b|choreographer\b|\bby \w)/i.test(t)) return t
          if (row.pieceType === 'Classical Variation') return `coached by ${t}`
          return `choreographed by ${t}`
        }

        // Front-side stamping. For solos, stamp the dancer name in the
        // "Dancer:" slot. For groups, stamp the piece name (a row of
        // dancer names would overflow). Always add the division below.
        const stampScorecard = (tplPage, row, xBase) => {
          const isGroup = row.entrants.length > 1
          const labelText = sanitize(isGroup
            ? (row.pieceTitle || '')
            : row.entrants.map(e => e.dancerName).filter(Boolean).join(' '))
          if (labelText) {
            // The visible name slot is bounded on the right by the
            // adjacent half's edge. Pad 24pt inside.
            const maxW = halfW - this.slot.dancerX - 24
            let size = this.slot.dancerSize
            while (size > 8 && fontBold.widthOfTextAtSize(labelText, size) > maxW) {
              size -= 1
            }
            tplPage.drawText(labelText, {
              x: xBase + this.slot.dancerX,
              y: this.slot.dancerY,
              size,
              font: fontBold,
              color: rgb(0, 0, 0)
            })
            const divisionText = sanitize(formatDivision(row.division))
            if (divisionText) {
              const maxDivisionW = halfW - this.slot.dancerX - 24
              let divisionSize = Math.max(9, Math.round(size * 0.65))
              while (divisionSize > 7 && fontItalic.widthOfTextAtSize(divisionText, divisionSize) > maxDivisionW) {
                divisionSize -= 1
              }
              tplPage.drawText(divisionText, {
                x: xBase + this.slot.dancerX,
                y: this.slot.dancerY - divisionSize - 4,
                size: divisionSize,
                font: fontItalic,
                color: rgb(0.35, 0.25, 0.15)
              })
            }
          }
        }

        // Back-side dancer-info slot occupying one half of a landscape
        // page (396 wide x 612 tall). Solos and 2-person groups show
        // their survey answers; groups of 3+ show only header info (too
        // many surveys to fit legibly).
        const drawBackSlot = (page, slotX, row) => {
          const sideMargin = 30
          const maxW = halfW - sideMargin * 2
          const centerX = slotX + halfW / 2

          // Lineup code ("B-10") in the top-right corner of the slot,
          // so the judges can quickly find the matching scorecard for
          // the current piece in the showcase order.
          const code = row.pieceId ? lineupCodes.get(row.pieceId) : null
          if (code) {
            const codeSize = 11
            const codeW = fontBold.widthOfTextAtSize(code, codeSize)
            page.drawText(code, {
              x: slotX + halfW - sideMargin - codeW,
              y: pageH - 30,
              size: codeSize,
              font: fontBold,
              color: rgb(0.4, 0.4, 0.4)
            })
          }
          let y = pageH - 80

          const drawCentered = (text, font, size, color) => {
            const t = sanitize(text)
            if (!t) return
            let sz = size
            while (sz > 8 && font.widthOfTextAtSize(t, sz) > maxW) sz -= 1
            const w = font.widthOfTextAtSize(t, sz)
            page.drawText(t, { x: centerX - w / 2, y, size: sz, font, color })
            y -= sz + 4
          }
          const drawCenteredWrapped = (text, font, size, color) => {
            const lines = wrap(sanitize(text), font, size, maxW)
            for (const line of lines) {
              const w = font.widthOfTextAtSize(line, size)
              page.drawText(line, { x: centerX - w / 2, y, size, font, color })
              y -= size + 5
            }
          }

          const isGroup = row.entrants.length > 1
          const dancerNameList = row.entrants.map(e => e.dancerName).filter(Boolean).join(', ')

          // Name: dancer for solos, piece title for groups (a comma-
          // separated dancer list would overflow the half-page header).
          drawCentered(isGroup ? row.pieceTitle : row.entrants[0].dancerName, fontBold, 26, rgb(0, 0, 0))
          y -= 2
          // Division
          drawCentered(formatDivision(row.division), fontItalic, 13, rgb(0.35, 0.25, 0.15))

          // Blank line, then Music & Teacher block.
          y -= 14
          if (!isGroup) {
            // For solos, surface the piece title here (groups already
            // have it as the Name above).
            drawCentered(row.pieceTitle, fontReg, 13, rgb(0.1, 0.1, 0.1))
          }
          const credit = teacherCreditFor(row)
          if (credit) {
            drawCentered(credit, fontReg, 11, rgb(0.35, 0.25, 0.15))
          }

          // Blank line before the Answers block.
          y -= 14

          // Groups of 3+ stop here: too many per-dancer surveys to
          // fit. Show the dancer roster comma-separated where the
          // answers would have gone.
          if (row.entrants.length > 2) {
            drawCenteredWrapped(dancerNameList, fontReg, 13, rgb(0.1, 0.1, 0.1))
            return
          }

          const questions = [
            { label: 'Most challenging movements', key: 'challengingMoments' },
            { label: 'Most joyful movements', key: 'joyfulMoments' },
            { label: 'Artistic risks', key: 'artisticRisks' },
            { label: 'Goal from competition', key: 'performanceGoal' }
          ]

          // Pre-measure the answers block; if a verbose dancer (e.g.
          // Patricia Dwyer) would overflow the slot, step the header
          // and answer sizes down together until it fits.
          const measureAnswersHeight = (hSize, aSize) => {
            let h = 0
            for (const e of row.entrants) {
              if (isGroup) h += hSize + 4
              const s = e.survey || {}
              for (const q of questions) {
                const ans = String(s[q.key] || '').trim()
                if (!ans) continue
                h += hSize + 4
                const lines = wrap(sanitize(ans), fontReg, aSize, maxW)
                h += lines.length * (aSize + 5)
                h += 6
              }
              h += 6
            }
            return h
          }
          const availableHeight = y - 30
          let headerSize = 12
          let answerSize = 11
          while (answerSize > 8.5 && measureAnswersHeight(headerSize, answerSize) > availableHeight) {
            answerSize -= 0.5
            headerSize -= 0.5
          }

          for (const e of row.entrants) {
            if (isGroup) {
              drawCentered(e.dancerName, fontBold, headerSize, rgb(0.3, 0.3, 0.3))
            }
            const s = e.survey || {}
            for (const q of questions) {
              const ans = String(s[q.key] || '').trim()
              if (!ans) continue
              drawCentered(q.label, fontBold, headerSize, rgb(0, 0, 0))
              drawCenteredWrapped(ans, fontReg, answerSize, rgb(0.15, 0.15, 0.15))
              y -= 6
            }
            y -= 6
          }
        }

        // Group rows by template so pairs share the same scorecard
        // page (a Classical row can't share a sheet with a Contemporary
        // row — the scoring criteria differ). Within each template
        // group, pair rowA + rowB and emit one front sheet + one
        // matching back sheet so duplex printing pairs them correctly.
        const templateKey = (division) => {
          const style = (division || '').split(' - ')[0]
          if (style === 'Classical Variation') return 'classical'
          if (style === 'Contemporary/Lyrical') return 'contemporary'
          return 'original'
        }
        // Each template has its scorecard on the LEFT half only; the
        // right half is intentionally blank so two scorecards can be
        // placed side by side. Embed once, then drawPage() twice per
        // sheet (at x=0 and x=halfW) to fill both halves.
        // Embed each judge's three templates separately — embedPages()
        // requires same-doc pages, and our 9 templates span 9 docs.
        const embedByJudge = {}
        for (const judge of this.judgeKeys) {
          const tpls = tplsByJudge[judge]
          const [classicalEmbed, contemporaryEmbed, originalEmbed] = await Promise.all([
            out.embedPage(tpls.classical.getPage(0)),
            out.embedPage(tpls.contemporary.getPage(0)),
            out.embedPage(tpls.original.getPage(0))
          ])
          embedByJudge[judge] = {
            classical: classicalEmbed,
            contemporary: contemporaryEmbed,
            original: originalEmbed
          }
        }

        // For preview, only emit Judge 1's section.
        const judgesToEmit = preview ? [this.judgeKeys[0]] : this.judgeKeys

        // Walk the lineup-sorted rows two at a time. Each half of the
        // sheet uses its OWN row's template (the templates have content
        // on their left half + blank right, so two different templates
        // can sit side-by-side on one landscape sheet).
        for (const judge of judgesToEmit) {
          const embedBy = embedByJudge[judge]
          for (let i = 0; i < rowsToProcess.length; i += 2) {
            const rowA = rowsToProcess[i]
            const rowB = rowsToProcess[i + 1]
            const embedA = embedBy[templateKey(rowA.division)]
            const embedB = rowB ? embedBy[templateKey(rowB.division)] : embedA

            // Front: stamp left template on left, right template on
            // right. Odd-count tail: blank scorecard on the right
            // using rowA's template type.
            const front = out.addPage([pageW, pageH])
            front.drawPage(embedA, { x: 0, y: 0 })
            front.drawPage(embedB, { x: halfW, y: 0 })
            stampScorecard(front, rowA, 0)
            if (rowB) {
              stampScorecard(front, rowB, halfW)
            }

            // Back: blank landscape page. Duplex printing mirrors the
            // sheet left-to-right, so the back's halves are SWAPPED
            // relative to the front — rowB on the left, rowA on the
            // right — so each dancer's scorecard (front) ends up
            // back-to-back with her own answers. Odd-count tail: rowA
            // sits alone on the front-left, so its answers go on the
            // back-right (left half blank).
            const back = out.addPage([pageW, pageH])
            if (rowB) {
              drawBackSlot(back, 0, rowB)
              drawBackSlot(back, halfW, rowA)
            } else {
              drawBackSlot(back, halfW, rowA)
            }
          }

          // Extras: 2 unstamped scorecards of each template type at the
          // end of every judge's section, for last-minute fillers or
          // replacements. Each extras sheet gets a blank back so duplex
          // print order doesn't drift into the next judge's section.
          if (!preview) {
            for (const key of ['classical', 'contemporary', 'original']) {
              const embed = embedBy[key]
              const front = out.addPage([pageW, pageH])
              front.drawPage(embed, { x: 0, y: 0 })
              front.drawPage(embed, { x: halfW, y: 0 })
              out.addPage([pageW, pageH])
            }
          }
        }

        const blob = new Blob([await out.save()], { type: 'application/pdf' })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = preview
          ? `iabf-${this.adminFestivalYear}-scorecard-preview.pdf`
          : `iabf-${this.adminFestivalYear}-personalized-scorecards.pdf`
        a.click()
        URL.revokeObjectURL(url)
        if (!preview) this.showScorecardPdfDialog = false
      } catch (e) {
        console.error('buildScorecardPdf failed:', e)
        this.scorecardPdfError = 'Build failed: ' + (e && e.message ? e.message : e)
      }
      this.buildingScorecardPdf = false
    },
    rebuildRows () {
      // Competition entries live on PIECES now (piece.competitionEntry +
      // piece.competitionEntrants), so a dancer can compete in both a solo
      // and a group piece. We iterate flagged pieces, then surface any
      // remaining dancers who flagged competitionRegistered but aren't yet
      // attached to a competing piece as orphan rows for the Pick Piece flow.
      const dancers = this.$store.getters.loadedIABFDancers
      const pieces = this.$store.getters.loadedIABFPieces
      const dancersById = new Map()
      for (const d of dancers) dancersById.set(d.id, d)
      const piecesById = new Map()
      for (const p of pieces) piecesById.set(p.id, p)

      const buildEntrant = d => {
        const rec = recommendDivision(d.competitionSurvey)
        return {
          dancerId: d.id,
          dancerName: [d.firstName, d.lastName].filter(Boolean).join(' '),
          firstName: d.firstName || '',
          lastName: d.lastName || '',
          email: d.email || '',
          survey: d.competitionSurvey || null,
          signals: extractSignals(d.competitionSurvey),
          recommendation: rec ? rec.division : ''
        }
      }

      const resultForRow = (row) => {
        const matches = (this.yearResults || []).filter(res => matchStoredResultToCompetitionRow(res, row))
        return matches[0] || null
      }
      const placementFor = (piece, row) => {
        const fromPiece = canonicalCompetitionPlacement(piece && piece.competitionPlace)
        if (fromPiece) {
          return fromPiece
        }
        const stored = resultForRow(row)
        return canonicalCompetitionPlacement(stored && stored.placement)
      }
      const scoreFor = (piece, row) => {
        const fromPiece = String((piece && piece.competitionScore) || '').trim()
        if (fromPiece) {
          return fromPiece
        }
        return competitionScoreFromResult(resultForRow(row))
      }

      const rows = []
      for (const piece of pieces) {
        if (!piece.competitionEntry) continue
        const entrantIds = Array.isArray(piece.competitionEntrants) ? piece.competitionEntrants : []
        const entrants = entrantIds
          .map(id => dancersById.get(id))
          .filter(Boolean)
          .map(buildEntrant)
          .sort((a, b) => a.dancerName.localeCompare(b.dancerName))
        if (entrants.length === 0) continue
        const row = {
          rowKey: 'piece-' + piece.id,
          pieceId: piece.id,
          pieceTitle: piece.title || '(untitled)',
          songArtist: piece.songArtist || '',
          division: piece.competition || '',
          pieceType: piece.pieceType || '',
          groupSize: piece.groupSize || '',
          programTeacher: piece.programTeacher || '',
          musicReceived: !!piece.musicReceived,
          entrants,
          placement: '',
          score: ''
        }
        row.placement = placementFor(piece, row)
        row.score = scoreFor(piece, row)
        rows.push(row)
      }

      // Orphans: dancers who registered a solo via the form whose registered
      // piece hasn't been turned into a competition entry yet. A dancer can be
      // in a group competition entry AND have a separate solo registration, so
      // we check HER OWN registered piece (competitionPieceId) — not whether she
      // appears on any entry. Keying on "any entry" would wrongly hide a group
      // member's solo. The registered piece is surfaced so the admin can confirm.
      for (const d of dancers) {
        if (!d.competitionRegistered) continue
        const soloPiece = d.competitionPieceId ? piecesById.get(d.competitionPieceId) : null
        const soloResolved = !!soloPiece &&
          soloPiece.competitionEntry === true &&
          Array.isArray(soloPiece.competitionEntrants) &&
          soloPiece.competitionEntrants.includes(d.id)
        if (soloResolved) continue
        const e = buildEntrant(d)
        rows.push({
          rowKey: 'orphan-' + d.id,
          pieceId: null,
          pieceTitle: '(none selected)',
          registeredPieceId: soloPiece ? soloPiece.id : null,
          registeredPieceTitle: soloPiece ? (soloPiece.title || '(untitled)') : '',
          songArtist: soloPiece ? (soloPiece.songArtist || '') : '',
          division: '',
          pieceType: soloPiece ? (soloPiece.pieceType || '') : '',
          groupSize: soloPiece ? (soloPiece.groupSize || '') : '',
          musicReceived: false,
          entrants: [e],
          placement: '',
          score: ''
        })
      }

      rows.sort((a, b) => {
        const an = a.entrants[0] ? a.entrants[0].dancerName : ''
        const bn = b.entrants[0] ? b.entrants[0].dancerName : ''
        return an.localeCompare(bn)
      })
      this.rows = rows
    },
    divisionColor (division) {
      const lower = (division || '').toLowerCase()
      if (lower.includes('classical')) return 'indigo'
      if (lower.includes('contemporary') || lower.includes('lyrical')) return 'deep-purple'
      if (lower.includes('original')) return 'teal darken-2'
      return 'blue-grey'
    },
    hexColorFor (division) {
      const lower = (division || '').toLowerCase()
      if (lower.includes('pending')) return '#9e9e9e'
      if (lower.includes('classical')) return '#3949ab'
      if (lower.includes('contemporary') || lower.includes('lyrical')) return '#5e35b1'
      if (lower.includes('original')) return '#00796b'
      return '#607d8b'
    },
    medalLabel (count) {
      if (this.useFullMedals) {
        if (count >= 3) return 'Gold / Silver / Bronze'
        if (count === 2) return 'Gold / Silver'
        if (count === 1) return 'Gold only'
        return ''
      }
      if (count >= 5) return 'Gold / Silver / Bronze'
      if (count >= 3) return 'Gold / Silver'
      if (count >= 1) return 'Gold only'
      return ''
    },
    medalPlacesFor (count) {
      if (this.useFullMedals) {
        if (count >= 3) return ['Gold', 'Silver', 'Bronze']
        if (count === 2) return ['Gold', 'Silver']
        if (count === 1) return ['Gold']
        return []
      }
      if (count >= 5) return ['Gold', 'Silver', 'Bronze']
      if (count >= 3) return ['Gold', 'Silver']
      if (count >= 1) return ['Gold']
      return []
    },
    async copyOneMedal (m) {
      // Just the place line ("1ST PLACE - BEG") — style and year are the
      // same across the whole order, so only the varying line is worth a
      // per-row copy.
      const text = m.line1
      try {
        await navigator.clipboard.writeText(text)
        this.copiedPlate = m.plate
        setTimeout(() => {
          if (this.copiedPlate === m.plate) this.copiedPlate = null
        }, 1800)
      } catch (e) {
        // eslint-disable-next-line no-alert
        window.prompt('Copy this:', text)
      }
    },
    downloadEngravingsCsv () {
      const escape = val => {
        const s = String(val == null ? '' : val)
        return (s.includes(',') || s.includes('"') || s.includes('\n'))
          ? '"' + s.replace(/"/g, '""') + '"'
          : s
      }
      const headers = ['Plate #', 'Line 1', 'Line 2', 'Line 3', 'Division']
      const rows = this.engravingMedals.map(m => [m.plate, m.line1, m.line2, m.line3, m.division])
      const csv = [headers, ...rows].map(r => r.map(escape).join(',')).join('\n')
      const blob = new Blob([csv], { type: 'text/csv' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `iabf-${this.adminFestivalYear}-medal-engravings.csv`
      a.click()
      URL.revokeObjectURL(url)
    },
    openReview (row) {
      this.reviewRow = row
      // Preselect the existing competition piece if it still exists. For an
      // orphan (no piece linked yet), fall back to the solo piece she registered
      // so the admin doesn't have to re-pick it.
      const stillExists = row.pieceId && this.allPieces.some(p => p.id === row.pieceId)
      const registeredExists = !row.pieceId && row.registeredPieceId && this.allPieces.some(p => p.id === row.registeredPieceId)
      this.pickedPieceId = stillExists ? row.pieceId : (registeredExists ? row.registeredPieceId : null)
      const parsed = parseExistingDivision(row.division)
      this.assignStyle = parsed.style
      this.assignLevel = parsed.level
      this.saveError = ''
      this.showReviewDialog = true
    },
    closeReview () {
      this.showReviewDialog = false
      this.reviewRow = null
      this.pickedPieceId = null
      this.assignStyle = ''
      this.assignLevel = ''
      this.saveError = ''
      this.changingPiece = false
      this.resetting = false
    },
    startChangePiece () {
      this.changingPiece = true
    },
    cancelChangePiece () {
      this.changingPiece = false
      this.pickedPieceId = this.reviewRow ? this.reviewRow.pieceId : null
    },
    async resetSurveyAndDivision () {
      if (!this.reviewRow || !this.reviewRow.pieceId || !this.reviewRow.entrants.length) return
      const dancerId = this.reviewRow.entrants[0].dancerId
      const pieceId = this.reviewRow.pieceId
      // eslint-disable-next-line no-alert
      if (!window.confirm('Clear this dancer’s self-assessment survey and reset the division on this piece? She will need to redo the survey.')) return
      this.resetting = true
      try {
        await this.$store.dispatch('clearCompetitionSurveyForDancer', { dancerId, useAdminYear: true })
        await this.$store.dispatch('updatePieceCompetitionDivision', { pieceId, division: 'None', useAdminYear: true })
        const piece = this.allPieces.find(p => p.id === pieceId)
        if (piece) piece.competition = 'None'
        const dancer = this.$store.getters.loadedIABFDancers.find(d => d.id === dancerId)
        if (dancer) dancer.competitionSurvey = null
        this.rebuildRows()
        this.assignStyle = ''
        this.assignLevel = ''
        // Refresh the open dialog with the updated row data.
        const refreshed = this.rows.find(r => r.rowKey === this.reviewRow.rowKey)
        if (refreshed) this.reviewRow = refreshed
      } catch (e) {
        console.error('resetSurveyAndDivision failed:', e)
        this.saveError = 'Could not reset. Please try again.'
      }
      this.resetting = false
    },
    applyRecommendationFor (rec) {
      if (!rec) return
      this.assignStyle = rec.style
      this.assignLevel = rec.level
    },
    async saveAssignment (andNext) {
      if (!this.reviewRow) return
      if (!this.pickedPieceId) {
        this.saveError = 'Pick a competition piece first.'
        return
      }
      this.saving = true
      this.saveError = ''
      const wasOrphan = !this.reviewRow.pieceId
      const oldPieceId = this.reviewRow.pieceId
      const dancerId = this.reviewRow.entrants[0] ? this.reviewRow.entrants[0].dancerId : null
      const previousRowKey = this.reviewRow.rowKey
      const newPieceId = this.pickedPieceId
      const isPieceChange = !wasOrphan && oldPieceId && newPieceId !== oldPieceId && dancerId
      try {
        // For an orphan row, add the dancer to the picked piece's
        // competitionEntrants list (also flips competitionEntry on).
        if (wasOrphan && dancerId) {
          await this.$store.dispatch('addDancerToCompetitionPiece', {
            pieceId: newPieceId,
            dancerId,
            useAdminYear: true
          })
        }
        // Solo piece-change: pull from old, add to new, clear the dancer's
        // survey (answers were specific to the old piece), and skip the
        // division write below since the change-flow already resets it.
        if (isPieceChange) {
          await this.$store.dispatch('removeDancerFromCompetitionPiece', {
            pieceId: oldPieceId,
            dancerId,
            useAdminYear: true
          })
          await this.$store.dispatch('addDancerToCompetitionPiece', {
            pieceId: newPieceId,
            dancerId,
            useAdminYear: true
          })
          await this.$store.dispatch('clearCompetitionSurveyForDancer', {
            dancerId,
            newPieceId,
            useAdminYear: true
          })
          // Reset division on the freshly picked piece so the admin
          // re-evaluates after the dancer redoes her survey.
          await this.$store.dispatch('updatePieceCompetitionDivision', {
            pieceId: newPieceId,
            division: 'None',
            useAdminYear: true
          })
          // Mirror state locally so the rebuilt rows are correct without a full reload.
          const oldPiece = this.allPieces.find(p => p.id === oldPieceId)
          if (oldPiece) {
            const next = (oldPiece.competitionEntrants || []).filter(id => id !== dancerId)
            oldPiece.competitionEntrants = next
            if (next.length === 0) {
              oldPiece.competitionEntry = false
              oldPiece.competition = 'None'
            }
          }
          const newPiece = this.allPieces.find(p => p.id === newPieceId)
          if (newPiece) {
            newPiece.competitionEntry = true
            const existing = Array.isArray(newPiece.competitionEntrants) ? newPiece.competitionEntrants : []
            if (!existing.includes(dancerId)) newPiece.competitionEntrants = [...existing, dancerId]
            newPiece.competition = 'None'
          }
          const dancer = this.$store.getters.loadedIABFDancers.find(d => d.id === dancerId)
          if (dancer) {
            dancer.competitionSurvey = null
            dancer.competitionPieceId = newPieceId
          }
        } else {
          const division = this.combinedDivision
          await this.$store.dispatch('updatePieceCompetitionDivision', {
            pieceId: newPieceId,
            division,
            useAdminYear: true
          })
          const piece = this.allPieces.find(p => p.id === newPieceId)
          if (piece) {
            piece.competition = division
            if (wasOrphan && dancerId) {
              piece.competitionEntry = true
              const existing = Array.isArray(piece.competitionEntrants) ? piece.competitionEntrants : []
              if (!existing.includes(dancerId)) {
                piece.competitionEntrants = [...existing, dancerId]
              }
            }
          }
        }
        this.rebuildRows()
        if (andNext) {
          this.advanceToNextUnassigned(previousRowKey)
        } else {
          this.closeReview()
        }
      } catch (e) {
        console.error('saveAssignment error:', e)
        this.saveError = 'Could not save. Please try again.'
      }
      this.saving = false
    },
    skipToNext () {
      const skipFromKey = this.reviewRow ? this.reviewRow.rowKey : null
      this.advanceToNextUnassigned(skipFromKey)
    },
    advanceToNextUnassigned (afterRowKey) {
      const idx = afterRowKey ? this.rows.findIndex(r => r.rowKey === afterRowKey) : -1
      const search = [
        ...this.rows.slice(idx + 1),
        ...this.rows.slice(0, Math.max(idx, 0))
      ]
      const next = search.find(r => this.isUnassigned(r) && r.pieceId)
      if (next) {
        this.openReview(next)
      } else {
        this.closeReview()
      }
    },
    dancersLabelForAward (award) {
      if (!award) {
        return ''
      }
      const row = this.rows.find(r => r.pieceId && r.pieceId === award.pieceId)
      if (row) {
        return (row.entrants || []).map(e => e.dancerName).filter(Boolean).join(' + ')
      }
      const imported = award.importedDancers || []
      if (!imported.length) {
        return ''
      }
      return imported.map(d => [d.firstName, d.lastName].filter(Boolean).join(' ')).filter(Boolean).join(' + ')
    },
    awardSavePayload (award) {
      const row = this.rows.find(r => r.pieceId && r.pieceId === award.pieceId)
      return {
        id: award.id,
        name: award.name,
        pieceId: award.pieceId || '',
        pieceTitle: row ? (row.pieceTitle || '') : '',
        dancers: row
          ? (row.entrants || []).map(e => ({
            dancerId: e.dancerId,
            firstName: e.firstName,
            lastName: e.lastName,
            email: e.email
          }))
          : []
      }
    },
    async persistSpecialAwards () {
      this.jcSaving = true
      this.jcNote = ''
      try {
        const saved = await this.$store.dispatch('saveCompetitionSpecialAwards', {
          year: this.adminFestivalYear,
          awards: this.specialAwards.map(a => this.awardSavePayload(a))
        })
        if (saved && Array.isArray(saved.results)) {
          this.yearResults = saved.results
        }
        if (saved && Array.isArray(saved.specialAwards)) {
          const importedById = {}
          this.specialAwards.forEach((a) => {
            if (a.importedDancers && a.importedDancers.length) {
              importedById[a.id] = a.importedDancers
            }
          })
          this.specialAwards = saved.specialAwards.map((a) => ({
            ...a,
            importedDancers: importedById[a.id] || []
          }))
        }
        this.jcNote = 'Saved.'
        window.setTimeout(() => {
          if (this.jcNote === 'Saved.') {
            this.jcNote = ''
          }
        }, 1600)
      } catch (e) {
        console.error('persistSpecialAwards failed:', e)
        this.jcNote = 'Could not save awards. Try again.'
      }
      this.jcSaving = false
    },
    async hydrateSpecialAwards () {
      let list = (this.specialAwards || []).slice()
      let derived = false
      if (!list.length) {
        list = specialAwardsFromImportedResults(this.yearResults)
        derived = list.length > 0
      }
      let matched = 0
      list = list.map((award) => {
        if (award.pieceId && this.rows.some(r => r.pieceId === award.pieceId)) {
          return award
        }
        const row = matchSpecialAwardToRow(award, this.rows)
        if (row && row.pieceId) {
          matched++
          return { ...award, pieceId: row.pieceId }
        }
        return award
      })
      this.specialAwards = list
      if (derived || matched) {
        await this.persistSpecialAwards()
        if (derived) {
          this.jcNote = list.length
            ? `Loaded ${list.length} Judges' Choice award(s) from imported ${this.adminFestivalYear} results.`
            : ''
        }
      }
    },
    async addAward () {
      const name = String(this.newAwardName || '').trim()
      if (!name) {
        return
      }
      const baseId = specialAwardIdFromName(name)
      const taken = new Set(this.specialAwards.map(a => a.id))
      let id = baseId
      let n = 2
      while (taken.has(id)) {
        id = baseId + '-' + n
        n++
      }
      this.specialAwards.push({ id, name, pieceId: '', importedDancers: [] })
      this.newAwardName = ''
      await this.persistSpecialAwards()
    },
    async removeAward (award) {
      if (!award) {
        return
      }
      // eslint-disable-next-line no-alert
      if (award.pieceId && !window.confirm('Remove this Judges’ Choice award?')) {
        return
      }
      this.specialAwards = this.specialAwards.filter(a => a.id !== award.id)
      await this.persistSpecialAwards()
    },
    async onAwardNameChange (award, event) {
      const name = String(event && event.target ? event.target.value : '').trim()
      if (!name) {
        if (event && event.target) {
          event.target.value = award.name
        }
        return
      }
      award.name = name
      await this.persistSpecialAwards()
    },
    async onAwardPieceChange (award, event) {
      award.pieceId = String(event && event.target ? event.target.value : '').trim()
      await this.persistSpecialAwards()
    },
    onPlaceChange (row, event) {
      const value = canonicalCompetitionPlacement(event && event.target ? event.target.value : '')
      row.placement = value
      this.saveRowResult(row)
    },
    onScoreChange (row, event) {
      const raw = event && event.target ? event.target.value : ''
      row.score = String(raw || '').trim()
      this.saveRowResult(row)
    },
    async saveRowResult (row, opts) {
      if (!row || !row.pieceId) {
        return
      }
      const silent = !!(opts && opts.silent)
      if (!silent) {
        this.resultSavingKey = row.rowKey
        this.resultError = ''
      }
      try {
        await this.$store.dispatch('saveCompetitionRowResult', {
          pieceId: row.pieceId,
          placement: row.placement || '',
          score: row.score || '',
          division: row.division || '',
          pieceTitle: row.pieceTitle || '',
          dancers: (row.entrants || []).map(e => ({
            dancerId: e.dancerId,
            firstName: e.firstName,
            lastName: e.lastName,
            email: e.email
          })),
          useAdminYear: true
        })
        const piece = this.allPieces.find(p => p.id === row.pieceId)
        if (piece) {
          piece.competitionPlace = row.placement || ''
          piece.competitionScore = row.score || ''
        }
        if (!silent) {
          this.yearResults = await this.$store.dispatch(
            'loadCompetitionResultsForYear',
            this.adminFestivalYear
          ) || this.yearResults
          this.resultSavedKey = row.rowKey
          window.setTimeout(() => {
            if (this.resultSavedKey === row.rowKey) {
              this.resultSavedKey = ''
            }
          }, 1600)
        }
      } catch (e) {
        console.error('saveRowResult failed:', e)
        this.resultError = 'Could not save place/score. Try again.'
        if (silent) {
          throw e
        }
      }
      if (!silent) {
        this.resultSavingKey = ''
      }
    },
    async applyImportedResults () {
      if (!(this.yearResults || []).length) {
        return
      }
      const toWrite = this.rows.filter((row) => {
        if (!row.pieceId) {
          return false
        }
        if (!row.placement && !row.score) {
          return false
        }
        const piece = this.allPieces.find(p => p.id === row.pieceId)
        const already = !!(piece && String(piece.competitionPlace || '').trim())
        return !already
      })
      if (!toWrite.length) {
        const filled = this.rows.filter(r => r.placement).length
        if (filled) {
          this.importFillNote = `${filled} place(s) already on ${this.adminFestivalYear} pieces.`
        }
        return
      }
      let saved = 0
      try {
        for (const row of toWrite) {
          await this.saveRowResult(row, { silent: true })
          saved++
        }
        this.yearResults = await this.$store.dispatch(
          'loadCompetitionResultsForYear',
          this.adminFestivalYear
        ) || this.yearResults
        this.rebuildRows()
        this.importFillNote =
          `Filled ${saved} place(s) from imported ${this.adminFestivalYear} results.`
      } catch (e) {
        this.importFillNote =
          saved
            ? `Filled ${saved}, then stopped: ${(e && e.message) || 'save failed'}.`
            : 'Could not apply imported results.'
      }
    },
    downloadCsv () {
      const escape = val => {
        const s = String(val == null ? '' : val)
        return (s.includes(',') || s.includes('"') || s.includes('\n'))
          ? '"' + s.replace(/"/g, '""') + '"'
          : s
      }
      const headers = ['Piece Title', 'Song / Artist', 'Dancers', 'Emails', 'Division', 'Place', 'Score', 'Recommended', 'Piece Type', 'Group Size', 'Music Received']
      const csvRows = this.rows.map(r => [
        r.pieceTitle,
        r.songArtist,
        r.entrants.map(e => e.dancerName).join(', '),
        r.entrants.map(e => e.email).filter(Boolean).join(', '),
        r.division || 'Pending',
        r.placement || '',
        r.score || '',
        r.entrants.map(e => e.recommendation).filter(Boolean).join(' / '),
        r.pieceType,
        r.groupSize,
        r.musicReceived ? 'Y' : 'N'
      ])
      const csv = [headers, ...csvRows].map(row => row.map(escape).join(',')).join('\n')
      const blob = new Blob([csv], { type: 'text/csv' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `iabf-${this.adminFestivalYear}-competition-registrations.csv`
      a.click()
      URL.revokeObjectURL(url)
    }
  }
}
</script>

<style scoped>
h2 {
  font-family: Quattrocento, serif;
  font-weight: 400;
  font-size: 28px;
  letter-spacing: -.02em;
  margin: 0;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 8px;
}
.page-hint {
  margin: 6px 0 0;
  font-size: 13px;
  color: #666;
}
.import-fill-note {
  color: #1a7a3c;
}
.jc-panel {
  background: #fff;
  border: 1px solid #e6e3df;
  border-radius: 6px;
  padding: 14px 16px 16px;
}
.jc-head {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 10px;
}
.jc-lede {
  margin: 4px 0 0;
}
.jc-empty {
  margin: 0 0 10px;
}
.jc-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  margin-bottom: 12px;
}
.jc-table th,
.jc-table td {
  text-align: left;
  padding: 8px 8px;
  border-bottom: 1px solid #f0ede8;
  vertical-align: middle;
}
.jc-table th {
  font-size: 11px;
  font-weight: 600;
  color: #999;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}
.jc-name-input,
.jc-piece-select {
  width: 100%;
  min-width: 10rem;
  padding: 6px 8px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font: inherit;
  background: #fff;
}
.jc-piece-select {
  max-width: 28rem;
}
.jc-dancers {
  color: #555;
  font-size: 13px;
}
.jc-del {
  border: none;
  background: transparent;
  color: #999;
  cursor: pointer;
  font-size: 14px;
  padding: 4px 8px;
  border-radius: 6px;
}
.jc-del:hover:not(:disabled) {
  background: #fdecea;
  color: #b00020;
}
.jc-del:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.jc-add {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
}
.jc-add .jc-name-input {
  max-width: 22rem;
}
.place-select {
  min-width: 7.5rem;
  padding: 4px 6px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font: inherit;
  background: #fff;
  color: #1a1a1a;
}
.place-select:disabled {
  color: #999;
  background: #f7f7f7;
}
.score-cell {
  display: flex;
  align-items: center;
  gap: 6px;
}
.score-input {
  width: 4.5rem;
  padding: 4px 6px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font: inherit;
  background: #fff;
}
.score-input:disabled {
  background: #f7f7f7;
  color: #999;
}
.result-status {
  font-size: 12px;
  color: #888;
}
.result-status.is-saved {
  color: #1a7a3c;
  font-weight: 700;
}

.summary-bar,
.division-bar {
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
}

.scorecard-panel {
  background: #fbf8f3;
  border: 1px solid #e6e1d4;
  border-radius: 6px;
  padding: 12px 14px;
}
.scorecard-head {
  display: flex;
  align-items: baseline;
  gap: 8px;
  margin-bottom: 8px;
}
.scorecard-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 10px;
}
.scorecard-cell {
  background: #fff;
  border: 1px solid #eee5d6;
  border-radius: 4px;
  padding: 8px 12px;
}
.scorecard-cell.scorecard-total {
  background: #fff8e1;
  border-color: #f4c63d;
}
.scorecard-label {
  font-family: Quattrocento, Georgia, serif;
  font-size: 12px;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #555;
  margin-bottom: 2px;
}
.scorecard-num {
  font-family: 'Fanwood Text', Georgia, serif;
  font-size: 18px;
  color: #1a1a1a;
}
.scorecard-num strong { font-weight: 700; }

.scorecard-skipped {
  margin-top: 12px;
  padding: 10px 12px;
  background: #fff4e6;
  border: 1px solid #f0c890;
  border-radius: 4px;
}
.scorecard-skipped-head {
  font-weight: 600;
  color: #6b3a00;
  margin-bottom: 6px;
}
.scorecard-skipped-list {
  margin: 0;
  padding-left: 18px;
  color: #1a1a1a;
}
.scorecard-skipped-list li {
  margin-bottom: 2px;
}

.scorecard-roster {
  margin-top: 12px;
}
.scorecard-roster summary {
  cursor: pointer;
}
.scorecard-roster-list {
  margin: 8px 0 0 0;
  padding-left: 28px;
  column-count: 3;
  column-gap: 16px;
}
.scorecard-roster-list li {
  break-inside: avoid;
  margin-bottom: 2px;
  font-size: 13px;
}

.scorecard-head {
  display: flex;
  align-items: baseline;
  gap: 8px;
  margin-bottom: 8px;
  flex-wrap: wrap;
}

.template-row {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 14px;
  padding-bottom: 10px;
  border-bottom: 1px dashed #e6e1d4;
}
.template-row:last-child { border-bottom: none; }

.judge-block {
  margin-top: 14px;
  padding: 10px 12px;
  background: #faf7f0;
  border: 1px solid #e6e1d4;
  border-radius: 4px;
}
.judge-heading {
  font-family: Quattrocento, Georgia, serif;
  font-weight: 700;
  font-size: 15px;
  color: #267E7D;
  margin-bottom: 6px;
}
.template-label {
  font-family: Quattrocento, Georgia, serif;
  font-weight: 700;
  font-size: 14px;
  color: #1a1a1a;
}

.slot-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 8px 16px;
  margin-top: 8px;
}
.slot-cell {
  display: flex;
  flex-direction: column;
  font-size: 12px;
  color: #555;
}
.slot-cell input {
  width: 100%;
  padding: 4px 6px;
  border: 1px solid #ccc;
  border-radius: 3px;
  font-size: 13px;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
}

.summary-stat,
.division-stat {
  display: flex;
  flex-direction: column;
  align-items: center;
  background: #f5f3f0;
  border-radius: 6px;
  padding: 10px 18px;
  min-width: 90px;
}

.division-stat {
  background: #ede7f6;
}

.medal-mode-toggle { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.medal-mode-label { font-family: 'Fanwood Text', Georgia, serif; font-size: 13px; color: #555; font-weight: 600; margin-right: 4px; }
.medal-mode-chip { background: #fff; border: 1px solid #ddd; border-radius: 999px; padding: 5px 14px; font-family: 'Fanwood Text', Georgia, serif; font-size: 13px; color: #1a1a1a; cursor: pointer; transition: all 0.12s; }
.medal-mode-chip:hover { background: #f5f3ed; }
.medal-mode-chip.active { background: #267E7D; color: #fff; border-color: #267E7D; font-weight: 700; }

.stat-num {
  font-family: Quattrocento, serif;
  font-size: 26px;
  font-weight: 700;
  color: #1a1a1a;
}

.stat-label {
  font-size: 12px;
  color: #888;
  margin-top: 2px;
  text-align: center;
}

.stat-medal {
  font-size: 11px;
  color: #6a4e1a;
  font-style: italic;
  margin-top: 4px;
}

.link {
  color: #267E7D;
  text-decoration: none;
}

.link:hover {
  text-decoration: underline;
}

.muted {
  color: #999;
  font-style: italic;
}

.muted-rec {
  color: #5d4037;
  font-size: 13px;
}

.warn-row {
  font-size: 13px;
  color: #b26a00;
  background: #fff3e0;
  border-radius: 4px;
  padding: 6px 10px;
}

.form-section-label {
  font-weight: 600;
  font-size: 13px;
  color: #555;
  margin: 12px 0 4px 0;
  text-transform: uppercase;
  letter-spacing: .04em;
  display: flex;
  align-items: center;
  gap: 6px;
}

.change-piece-row {
  margin-top: 8px;
}

.change-piece-warning {
  font-size: 13px;
  color: #b04a00;
  background: #fff4e5;
  border: 1px solid #f4c63d;
  border-radius: 4px;
  padding: 8px 10px;
}

.assignment-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.preview-row {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;
}

.preview-label {
  font-size: 13px;
  color: #666;
}

.recommendation-box {
  background: #fff8e1;
  border: 1px solid #ffe082;
  border-radius: 6px;
  padding: 12px 16px;
  margin: 12px 0;
}

.recommendation-header {
  font-weight: 600;
  color: #5d4037;
  margin-bottom: 6px;
  display: flex;
  align-items: center;
  gap: 6px;
}

.recommendation-reasons {
  margin: 4px 0 8px 16px;
  padding: 0;
  font-size: 13px;
  color: #5d4037;
}

.recommendation-reasons li {
  margin-bottom: 2px;
}

.recommendation-entrant {
  padding: 4px 0;
  border-top: 1px dashed #ffe082;
  margin-top: 4px;
}
.recommendation-entrant:first-of-type {
  border-top: none;
  margin-top: 0;
}
.recommendation-entrant-head {
  font-size: 14px;
  color: #5d4037;
  display: flex;
  align-items: center;
}

.entrant-survey {
  margin-top: 8px;
}
.entrant-survey-head {
  font-weight: 700;
  font-size: 13px;
  color: #1a1a1a;
  margin: 8px 0 4px 0;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.entrant-line {
  padding: 2px 0;
}
.entrant-line + .entrant-line {
  border-top: 1px dashed #ece9e2;
  padding-top: 4px;
  margin-top: 4px;
}

.entry-badge {
  display: inline-block;
  font-size: 11px;
  color: #5d4037;
  background: #fff4e5;
  border: 1px solid #f5d6a8;
  border-radius: 3px;
  padding: 1px 6px;
  margin-top: 4px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.group-tag {
  font-size: 12px;
  color: #5d4037;
  background: #fff4e5;
  border: 1px solid #f5d6a8;
  border-radius: 3px;
  padding: 2px 8px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.survey-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
}

.survey-table td {
  padding: 8px 10px;
  border-bottom: 1px solid #f0ede8;
  vertical-align: top;
}

.survey-q {
  width: 38%;
  color: #777;
  font-weight: 600;
}

.survey-a {
  color: #1a1a1a;
}

/* Division-grouped view */
.division-view {
  margin-top: 8px;
}

.division-card {
  background: #fff;
  border: 1px solid #e6e3df;
  border-radius: 6px;
  margin-bottom: 18px;
  overflow-x: auto;
}

.division-card-header {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  padding: 12px 16px;
  background: #faf9f7;
  border-bottom: 1px solid #eee;
  border-left: 4px solid #607d8b;
}

.division-card-title {
  font-family: Quattrocento, serif;
  font-weight: 700;
  font-size: 18px;
  color: #1a1a1a;
}

.division-card-count {
  font-weight: 400;
  font-size: 14px;
  color: #888;
  margin-left: 6px;
}

.division-card-medal {
  font-size: 12px;
  color: #6a4e1a;
  font-style: italic;
}

.competitor-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}

.competitor-table thead {
  background: #fafafa;
}

.competitor-table th {
  text-align: left;
  padding: 8px 10px;
  font-size: 11px;
  font-weight: 600;
  color: #999;
  text-transform: uppercase;
  letter-spacing: .04em;
  border-bottom: 1px solid #eee;
}

.competitor-table td {
  padding: 10px;
  border-bottom: 1px solid #f3f1ed;
  vertical-align: middle;
}

.competitor-table tr:last-child td {
  border-bottom: none;
}

.signal-pill {
  display: inline-block;
  font-size: 11px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 10px;
  background: #eceff1;
  color: #455a64;
  white-space: nowrap;
}

.pill-1 { background: #ffebee; color: #b71c1c; }
.pill-2 { background: #fff3e0; color: #e65100; }
.pill-3 { background: #f1f8e9; color: #33691e; }
.pill-4 { background: #e3f2fd; color: #0d47a1; }
.pill-5 { background: #ede7f6; color: #4527a0; }
.pill-flat { background: #fff3e0; color: #e65100; }
.pill-pointe { background: #fce4ec; color: #ad1457; }
.pill-gold { background: #fff8e1; color: #5d4037; font-size: 14px; padding: 2px 6px; }

/* Engravings dialog */
.engraving-summary {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}

.engraving-stat {
  display: flex;
  flex-direction: column;
  align-items: center;
  background: #faf8f4;
  border-radius: 6px;
  padding: 8px 16px;
  min-width: 80px;
}

.engraving-group {
  margin-bottom: 16px;
  border: 1px solid #eee;
  border-radius: 6px;
  overflow: hidden;
}

.engraving-group-header {
  padding: 8px 12px;
  background: #faf9f7;
  border-bottom: 1px solid #eee;
  font-size: 14px;
}

.engraving-list { display: flex; flex-direction: column; gap: 8px; }
.engraving-row { display: flex; align-items: center; gap: 14px; background: #faf8f3; border: 1px solid #ece5d2; border-radius: 6px; padding: 10px 14px; }
.engraving-plate { font-family: Quattrocento, Georgia, serif; font-weight: 700; font-size: 15px; color: #1a4452; min-width: 48px; }
.engraving-text { flex: 1; margin: 0; font-family: 'SF Mono', Menlo, Consolas, monospace; font-size: 13px; line-height: 1.45; color: #1a1a1a; white-space: pre; }
.engraving-copy-btn { background: #fff; border: 1px solid #267E7D; color: #267E7D; border-radius: 4px; padding: 6px 14px; font-family: 'Fanwood Text', Georgia, serif; font-size: 13px; font-weight: 700; cursor: pointer; white-space: nowrap; transition: background .12s, color .12s; }
.engraving-copy-btn:hover { background: #eef6f8; }
.engraving-copy-btn.is-flashed { background: #267E7D; color: #fff; }

.place-pill {
  display: inline-block;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: .04em;
  padding: 2px 8px;
  border-radius: 10px;
}

.place-gold { background: #fff8e1; color: #6a4e00; }
.place-silver { background: #eceff1; color: #455a64; }
.place-bronze { background: #efebe9; color: #5d4037; }
</style>
